import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Users,
  Radio,
  FileText,
  Send,
  AlertTriangle,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { cn, formatTimestamp, formatDateTime, getSeverityColor, getSeverityBg, getStatusColor, API_BASE_URL } from '../lib/utils';
import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../store';
import type { MajorIncident } from '../types';

export default function MajorIncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);

  const [majorIncident, setMajorIncident] = useState<MajorIncident | null>(null);
  const [loading, setLoading] = useState(true);
  const [newLogEntry, setNewLogEntry] = useState('');
  const [addingLog, setAddingLog] = useState(false);
  const [newResourceId, setNewResourceId] = useState('');
  const [addingResource, setAddingResource] = useState(false);
  const [newIncidentId, setNewIncidentId] = useState('');
  const [addingIncident, setAddingIncident] = useState(false);

  const fetchMajorIncident = useCallback(async () => {
    if (!token || !id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/major-incidents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMajorIncident(data.majorIncident);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { fetchMajorIncident(); }, [fetchMajorIncident]);

  const patchMajorIncident = async (updates: Partial<MajorIncident>) => {
    if (!token || !id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/major-incidents/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        setMajorIncident(data.majorIncident);
      }
    } catch { /* ignore */ }
  };

  const handleAddLog = async () => {
    if (!newLogEntry.trim() || !token || !id) return;
    setAddingLog(true);
    try {
      const res = await fetch(`${API_BASE_URL}/major-incidents/${id}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ details: newLogEntry.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setMajorIncident(data.majorIncident);
        setNewLogEntry('');
      }
    } catch { /* ignore */ } finally {
      setAddingLog(false);
    }
  };

  const handleAddResource = () => {
    if (!newResourceId.trim() || !majorIncident) return;
    const updated = [...(majorIncident.assignedResources || []), newResourceId.trim()];
    patchMajorIncident({ assignedResources: updated });
    setNewResourceId('');
    setAddingResource(false);
  };

  const handleRemoveResource = (resourceId: string) => {
    if (!majorIncident) return;
    const updated = (majorIncident.assignedResources || []).filter((r) => r !== resourceId);
    patchMajorIncident({ assignedResources: updated });
  };

  const handleAddIncident = () => {
    if (!newIncidentId.trim() || !majorIncident) return;
    const updated = [...(majorIncident.assignedIncidents || []), newIncidentId.trim()];
    patchMajorIncident({ assignedIncidents: updated });
    setNewIncidentId('');
    setAddingIncident(false);
  };

  const handleRemoveIncident = (incidentId: string) => {
    if (!majorIncident) return;
    const updated = (majorIncident.assignedIncidents || []).filter((i) => i !== incidentId);
    patchMajorIncident({ assignedIncidents: updated });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!majorIncident) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3">
        <AlertTriangle className="w-8 h-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Major incident {id} not found</p>
        <button onClick={() => navigate(-1)} className="text-xs text-primary hover:underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <AlertTriangle className="w-5 h-5 text-destructive" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-mono text-muted-foreground">{majorIncident.id}</span>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', getSeverityBg(majorIncident.severity), getSeverityColor(majorIncident.severity))}>
              {majorIncident.severity.toUpperCase()}
            </span>
            <span className={cn('text-[10px] font-medium', getStatusColor(majorIncident.status))}>
              {majorIncident.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <h1 className="text-sm font-bold text-foreground truncate">{majorIncident.title}</h1>
        </div>
      </div>

      {/* Content grid */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Main Content */}
        <div className="lg:col-span-2 flex flex-col gap-3 min-h-0">
          {/* Description */}
          <div className="glass-panel p-3 flex-shrink-0">
            <p className="text-xs text-muted-foreground leading-relaxed">{majorIncident.description}</p>
            {majorIncident.tags && majorIncident.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {majorIncident.tags.map((tag) => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* METHAN Report */}
          <div className="glass-panel p-3 flex-shrink-0">
            <h2 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> METHAN Report
              <span className="text-[9px] text-muted-foreground font-normal ml-2">(Major Incident Initial Report)</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Message</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.message || 'Not provided'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Exact Location</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.exactLocation || 'Not provided'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Time</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.time || 'Not provided'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Access</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.access || 'Not provided'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Numbers</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.numbers || 'Not provided'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Hazards</div>
                <div className="text-xs text-foreground bg-background border border-border rounded px-2 py-1.5">
                  {majorIncident.methan?.hazards || 'Not provided'}
                </div>
              </div>
            </div>
          </div>

          {/* Activity Log */}
          <div className="glass-panel flex flex-col flex-1 min-h-0">
            <div className="px-3 py-2 border-b border-border flex-shrink-0">
              <h2 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Activity Log
              </h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
              {(majorIncident.logs || []).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">No log entries yet.</p>
              )}
              {(majorIncident.logs || []).map((log) => (
                <div key={log.id} className="flex gap-2">
                  <div className="flex flex-col items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                    <div className="w-px flex-1 bg-border mt-1" />
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-medium text-foreground">{log.userName}</span>
                      <span className="text-[10px] px-1 py-0.5 rounded bg-accent text-muted-foreground">{log.action}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{log.details}</p>
                    <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{formatDateTime(log.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Add Log Entry */}
            <div className="px-3 py-2 border-t border-border flex-shrink-0">
              <div className="flex gap-1.5">
                <input
                  value={newLogEntry}
                  onChange={(e) => setNewLogEntry(e.target.value)}
                  placeholder="Add log entry..."
                  className="flex-1 px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddLog()}
                  disabled={addingLog}
                />
                <button onClick={handleAddLog} disabled={addingLog || !newLogEntry.trim()} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
                  {addingLog ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="min-h-0 overflow-y-auto space-y-2">
          {/* Location */}
          <div className="glass-panel p-3">
            <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> Location
            </h3>
            <p className="text-xs text-foreground">{majorIncident.location?.address || 'No address'}</p>
            {majorIncident.location?.latitude && majorIncident.location?.longitude && (
              <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                {majorIncident.location.latitude.toFixed(4)}, {majorIncident.location.longitude.toFixed(4)}
              </p>
            )}
          </div>

          {/* Timeline */}
          <div className="glass-panel p-3">
            <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Timeline
            </h3>
            <div className="space-y-1 text-[10px]">
              <div className="flex justify-between"><span className="text-muted-foreground">Declared</span><span className="text-foreground font-mono">{formatTimestamp(majorIncident.declaredAt)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">By</span><span className="text-foreground">{majorIncident.declaredBy}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Updated</span><span className="text-foreground font-mono">{formatTimestamp(majorIncident.updatedAt)}</span></div>
            </div>
          </div>

          {/* Commanders */}
          <div className="glass-panel p-3">
            <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Commanders
            </h3>
            <div className="space-y-2">
              {(majorIncident.commanders || []).map((cmd) => (
                <div key={cmd.id} className="px-2 py-1.5 rounded-lg bg-accent/50">
                  <p className="text-xs font-medium text-foreground">{cmd.name}</p>
                  <p className="text-[10px] text-muted-foreground">{cmd.role} • {cmd.department}</p>
                </div>
              ))}
              {(majorIncident.commanders || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-2">No commanders assigned</p>
              )}
            </div>
          </div>

          {/* Assigned Resources */}
          <div className="glass-panel p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Radio className="w-3 h-3" /> Resources ({(majorIncident.assignedResources || []).length})
              </h3>
              <button onClick={() => setAddingResource(true)} className="p-0.5 rounded hover:bg-accent transition-colors" title="Add resource">
                <Plus className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
            {addingResource && (
              <div className="flex gap-1 mb-2">
                <input
                  value={newResourceId}
                  onChange={(e) => setNewResourceId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddResource()}
                  placeholder="Resource ID..."
                  className="flex-1 px-2 py-1 rounded bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  autoFocus
                />
                <button onClick={handleAddResource} className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Plus className="w-3 h-3" />
                </button>
                <button onClick={() => { setAddingResource(false); setNewResourceId(''); }} className="p-1 rounded hover:bg-accent transition-colors">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            )}
            <div className="space-y-1">
              {(majorIncident.assignedResources || []).map((resourceId) => (
                <div key={resourceId} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent/50 group">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-xs text-foreground font-mono flex-1">{resourceId}</span>
                  <button onClick={() => handleRemoveResource(resourceId)} className="p-0.5 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-all" title="Remove resource">
                    <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ))}
              {(majorIncident.assignedResources || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-2">No resources assigned</p>
              )}
            </div>
          </div>

          {/* Related Incidents */}
          <div className="glass-panel p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Related Incidents ({(majorIncident.assignedIncidents || []).length})
              </h3>
              <button onClick={() => setAddingIncident(true)} className="p-0.5 rounded hover:bg-accent transition-colors" title="Add incident">
                <Plus className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
            {addingIncident && (
              <div className="flex gap-1 mb-2">
                <input
                  value={newIncidentId}
                  onChange={(e) => setNewIncidentId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddIncident()}
                  placeholder="Incident ID..."
                  className="flex-1 px-2 py-1 rounded bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  autoFocus
                />
                <button onClick={handleAddIncident} className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Plus className="w-3 h-3" />
                </button>
                <button onClick={() => { setAddingIncident(false); setNewIncidentId(''); }} className="p-1 rounded hover:bg-accent transition-colors">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            )}
            <div className="space-y-1">
              {(majorIncident.assignedIncidents || []).map((incidentId) => (
                <div key={incidentId} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent/50 group">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span className="text-xs text-foreground font-mono flex-1">{incidentId}</span>
                  <button onClick={() => handleRemoveIncident(incidentId)} className="p-0.5 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-all" title="Remove incident">
                    <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ))}
              {(majorIncident.assignedIncidents || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-2">No incidents linked</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
